import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule } from '@angular/core';
import * as i1$2 from '@ngrx/effects';
import { createEffect, ofType, EffectsModule } from '@ngrx/effects';
import * as i8 from '@ngrx/store';
import { createAction, props, createFeatureSelector, createSelector, select, createReducer, on, StoreModule } from '@ngrx/store';
import * as i5 from '@upr-web-primer/gds-button-events';
import { GdsButtonEventsModule } from '@upr-web-primer/gds-button-events';
import { DOCUMENT } from '@angular/common';
import * as i2 from '@upr-web-primer/app-utils';
import { paramBool, param } from '@upr-web-primer/app-utils';
import * as i4 from '@upr-web-primer/cookie-setting-modal';
import * as i1$1 from '@upr-web-primer/digital-data';
import * as i3 from '@upr-web-primer/gds-common-utility';
import * as i6 from '@upr-web-primer/geolocation';
import * as i9 from '@upr-web-primer/tcm-service';
import { WINDOW } from '@upr-web-primer/window';
import { get } from 'lodash-es';
import { BehaviorSubject, Subscription, Subject, takeUntil, filter, map, of, switchMap, catchError, tap, from } from 'rxjs';
import * as i1 from '@upr-web-primer/gds-data-model';

const createFooterNavSiteLinks = (data) => {
    const gdsBrandUtility = createBrandUtility(data);
    const gdsNavigationLinks = createNavigationLinks(data);
    const components = [gdsBrandUtility, ...gdsNavigationLinks].filter(Boolean);
    if (components.length === 0) {
        return null;
    }
    const gdsComponentData = {
        selector: 'gds-footer-nav-site-links',
        data: {
            components,
        },
    };
    return {
        id: 'nav-site-links',
        components: [gdsComponentData],
    };
};
const createFooterAppLinks = (data) => {
    const gdsHeading = createHeading({
        heading: get(data, 'heading', ''),
        classList: 'heading',
        classes: 'heading-7',
        tagtype: 'span',
        type: 'left',
    });
    const gdsImageLinks = createImageLinks(get(data, 'imageButton', []));
    const gdsBody = createBody({
        description: get(data, 'description', ''),
        class: 'description',
        classes: 'body-2',
        Type: 'description',
    });
    const gdsDropdown = createDropdown(get(data, 'language', null));
    const langComponents = [gdsBody, gdsDropdown];
    const gdsFooterLang = createLanguageSection(langComponents);
    const components = [gdsHeading, gdsImageLinks, gdsFooterLang].filter(Boolean);
    if (components.length === 0) {
        return null;
    }
    const gdsComponentData = {
        selector: 'gds-app-links',
        components,
    };
    return {
        id: 'app-links',
        components: [gdsComponentData],
    };
};
const createFooterUtility = (data) => {
    const utilityLinks = get(data, 'buttons', []);
    const trademarkDescription = get(data, 'description', '');
    const trademarkLegalDescription = get(data, 'legaldescription', '');
    const gdsUtilityLinks = createUtilityLinks(utilityLinks);
    const gdsTrademark = createTrademark(trademarkDescription, trademarkLegalDescription);
    const components = [gdsUtilityLinks, gdsTrademark].filter(Boolean);
    if (components.length === 0) {
        return null;
    }
    const gdsComponentData = {
        selector: 'gds-footer-utility',
        components,
    };
    return {
        id: 'utility',
        components: [createDivider('horizontal'), gdsComponentData],
    };
};
const createNavigationLinks = (data) => {
    const navigationLinks = get(data, 'navigationLinks', null);
    if (!navigationLinks) {
        return [];
    }
    const isAccordion = ['true', true].includes(get(navigationLinks, 'accordian'));
    const navLinks = get(navigationLinks, 'navLinks', []);
    const components = [];
    const mainCtaListSections = [];
    const ctaListStyle = {
        context: 'footer-nav-site-links',
        orientation: 'vertical',
    };
    navLinks.forEach((list) => {
        const heading = get(list, 'heading', '');
        const links = get(list, 'subSectionMenu', []);
        const headingBtnData = {
            label: heading,
            type: 'heading',
        };
        mainCtaListSections.push(createCtaListSection([headingBtnData, ...links]));
        if (isAccordion) {
            const accordionData = {
                heading,
                headerOption: 'tertiary',
            };
            const ctaListSection = createCtaListSection(links);
            const gdsCtaList = createCtaList([ctaListSection], ctaListStyle);
            const gdsAccordion = createAccordion(accordionData, [gdsCtaList], 'd-none-xlarge');
            components.push(gdsAccordion);
        }
    });
    const maxColumns = Math.min(mainCtaListSections.filter(Boolean).length, 5);
    ctaListStyle.context += ' gds-cta-list-cols-' + maxColumns;
    const mainCtaListClassList = isAccordion ? 'd-none d-grid-xlarge' : '';
    const mainCtaList = createCtaList(mainCtaListSections, ctaListStyle, mainCtaListClassList);
    components.unshift(mainCtaList);
    return components.filter(Boolean);
};
const createBrandUtility = (data) => {
    const brandLogo = get(data, 'imageButton[0]', null);
    const socialLinks = get(data, 'socialLinks', []).map((link) => ({
        ...link,
        type: 'global-nav-button',
    }));
    const gdsBrandLogo = createImageLink(brandLogo);
    const socialLinksSection = createCtaListSection(socialLinks);
    const gdsSocialList = createCtaList([socialLinksSection], { context: 'button-element', orientation: 'horizontal' }, 'gds-brand-utility-social-links');
    const components = [gdsBrandLogo, gdsSocialList].filter(Boolean);
    if (components.length === 0) {
        return null;
    }
    return {
        selector: 'gds-brand-utility',
        data: {
            components,
        },
    };
};
const createUtilityLinks = (buttons) => {
    if (buttons.length === 0) {
        return null;
    }
    const components = buttons.map((btnData) => {
        const button = {
            ...btnData,
            labelClass: btnData.target === 'privacy' ? 'ot-sdk-show-settings' : '',
        };
        return {
            selector: 'gds-button',
            data: {
                button,
            },
        };
    });
    return {
        selector: 'gds-footer-utility-links',
        components,
    };
};
const createTrademark = (description, legalDescription) => {
    const gdsDescription = createBody({
        classList: 'description',
        description,
    });
    const gdsLegalDescription = createBody({
        classList: 'legal-description',
        legalDescription,
    });
    const components = [gdsDescription, gdsLegalDescription].filter(Boolean);
    if (components.length === 0) {
        return null;
    }
    return {
        selector: 'gds-footer-trademark',
        components,
    };
};
const createLanguageSection = (langComponents) => {
    const components = langComponents.filter(Boolean);
    if (components.length === 0) {
        return null;
    }
    return {
        selector: 'gds-footer-language',
        class: 'rtf-dropdown',
        components,
    };
};
const createAccordion = (data, components, classList = '') => {
    if (components.length === 0) {
        return null;
    }
    return {
        selector: 'gds-accordion',
        data,
        classList,
        components,
    };
};
const createDropdown = (data) => {
    if (!data) {
        return null;
    }
    const defaultValue = get(data, 'defaultValue', '');
    const heading = get(data, 'heading', '');
    const labelIcon = get(data, 'icon', '');
    const preventValueChange = get(data, 'props.preventValueChange', true);
    const options = get(data, 'listItem', []).map((opt) => ({
        label: opt.label,
        value: opt.label,
        link: {
            ...opt,
            label: undefined,
        },
    }));
    if (options.length === 0) {
        return null;
    }
    return {
        selector: 'gds-dropdown',
        version: 'v2-light',
        class: 'dropdown',
        value: defaultValue,
        heading,
        data: {
            dropdownControl: {
                labelIcon,
            },
            options,
        },
        props: {
            preventValueChange
        }
    };
};
const createCtaListSection = (buttons) => {
    if (buttons.length === 0) {
        return null;
    }
    return {
        buttons,
    };
};
const createCtaList = (sections, style, classList = '') => {
    const populatedSections = sections.filter(Boolean);
    if (populatedSections.length === 0) {
        return null;
    }
    return {
        selector: 'gds-cta-list',
        classList,
        props: {
            contrast: true,
        },
        data: {
            style,
            sections: populatedSections,
        },
    };
};
const createImageLink = (data) => {
    if (!data) {
        return null;
    }
    return {
        selector: 'gds-image-link',
        data,
    };
};
const createImageLinks = (imageButton) => {
    if (imageButton.length === 0) {
        return null;
    }
    return {
        selector: 'gds-image-links',
        data: {
            imageButton,
        },
        class: 'images',
    };
};
const createHeading = (data) => {
    const { heading, classes, classList, tagtype, type } = data;
    if (!heading) {
        return null;
    }
    return {
        selector: 'gds-heading',
        data: {
            heading,
        },
        class: classList,
        classes,
        tagtype,
        type,
    };
};
const createBody = (data) => {
    const { description, legalDescription, classes, classList, type } = data;
    if (!description && !legalDescription) {
        return null;
    }
    return {
        selector: 'gds-body',
        data: {
            description,
            legalDescription,
        },
        class: classList,
        classes,
        type,
    };
};
const createDivider = (orientation) => {
    return {
        selector: 'gds-global-nav-divider',
        data: {
            orientation,
        },
    };
};

class GdsFooterParserService {
    constructor(dataService) {
        this.dataService = dataService;
        this.hasCookieSettingModal$ = new BehaviorSubject(false);
    }
    parseTridionData(content) {
        if (!content) {
            return null;
        }
        const rawSections = get(content, 'sections', []);
        const parsedSections = rawSections.map((section) => this.transformSection(section));
        return {
            sections: parsedSections.filter(Boolean),
        };
    }
    transformSection(rawSection) {
        const selector = get(rawSection, 'sectionType', '');
        if (selector === 'gds-footer-nav-site-links') {
            return this.parseNavigationLinks(rawSection);
        }
        else if (selector === 'gds-app-links') {
            return this.parseAppLinks(rawSection);
        }
        else if (selector === 'gds-footer-utility') {
            return this.parseFooterUtility(rawSection);
        }
        return null;
    }
    parseNavigationLinks(rawSection) {
        const parsedSection = this.dataService.parseContent({ ...rawSection });
        const rawNavigationLinks = get(parsedSection, 'navigationLinks[0]', {});
        const rawNavLinks = get(rawNavigationLinks, 'navLinks', []);
        const parsedNavLinks = rawNavLinks.map((navLinksSection) => {
            const buttons = get(navLinksSection, 'subSectionMenu', []);
            const parsedButtons = this.dataService.parseContent({ buttons }).buttons;
            return {
                ...navLinksSection,
                subSectionMenu: parsedButtons,
            };
        });
        const rawSocialLinks = get(rawSection, 'socialLinks', []);
        const parsedSocialLinks = this.dataService.parseContent({
            buttons: rawSocialLinks,
        }).buttons;
        const section = {
            ...parsedSection,
            socialLinks: parsedSocialLinks,
            navigationLinks: {
                ...rawNavigationLinks,
                navLinks: parsedNavLinks,
            },
        };
        return createFooterNavSiteLinks(section);
    }
    parseAppLinks(rawSection) {
        const parsedSection = this.dataService.parseContent({ ...rawSection });
        const rawLanguageData = get(parsedSection, 'language[0].dropdownMenu[0]', {});
        const rawLanguageOptions = get(rawLanguageData, 'listItem', []);
        const parsedLanguageOptions = rawLanguageOptions.length ? this.dataService.parseContent({
            buttons: rawLanguageOptions,
        }).buttons : [];
        const section = {
            ...parsedSection,
            language: { ...rawLanguageData, listItem: parsedLanguageOptions, props: { preventValueChange: true } },
        };
        return createFooterAppLinks(section);
    }
    parseFooterUtility(rawSection) {
        const section = this.dataService.parseContent({ ...rawSection });
        const buttons = get(section, 'buttons', []);
        const hasCookieModal = buttons.find((btn) => btn.target === 'privacy');
        this.hasCookieSettingModal$.next(hasCookieModal);
        return createFooterUtility(section);
    }
}
GdsFooterParserService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsFooterParserService, deps: [{ token: i1.GdsDataModelService }], target: i0.ɵɵFactoryTarget.Injectable });
GdsFooterParserService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsFooterParserService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsFooterParserService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.GdsDataModelService }]; } });

const GDS_FOOTER_REPLACED_DEFAULTS = {
    gds_footer: {
        enabled: paramBool('$gds_footer.enabled$'),
        content_id: param('$gds_footer.content_id$'),
        cookie_name: param('$gds_footer.cookie_name$'),
        use_tridion_config: paramBool('$gds_global_nav.use_tridion_config$') ||
            paramBool('$gds_footer.use_tridion_config$'),
    },
};
const GDS_FOOTER_ENV_DEFAULTS = {
    gds_footer: {
        enabled: false,
        content_id: 'GDS-Footer-Utility',
        cookie_name: 'display_global_footer',
        use_tridion_config: false,
    },
};

const getContentId = createAction('[Content Id] Get GDS Footer Content Id');
const getContentIdSuccess = createAction('[Content Id] Get GDS Footer Content Id Success', props());
const getContentIdFailure = createAction('[Content Id] Get GDS Footer Content Id Failure', props());
const getContentSuccess = createAction('[Tridion Content] GDS Footer Tridion Content Success', props());
const getContentFailure = createAction('[Tridion Content] GDS Footer Tridion Content Failure', props());
const parseContentSuccess = createAction('[Tridion Content] GDS Footer Parse Tridion Content Success', props());
const parseContentFailure = createAction('[Tridion Content] GDS Footer Parse Tridion Content Failure', props());

const FooterFeatureKey = 'gds-footer';

const gdsFooterState = createFeatureSelector(FooterFeatureKey);
const rawSections = createSelector(gdsFooterState, (state) => state.tridionContent.rawContent?.sections);
const view = createSelector(gdsFooterState, (state) => state.view);

class GdsFooterService {
    constructor(appDataLayer, appEnvSrv, commonUtilityService, cookieSettingModalService, digitalData, gdsButtonEventsService, geolocationService, parser, store, tcmService, document, window) {
        this.appDataLayer = appDataLayer;
        this.appEnvSrv = appEnvSrv;
        this.commonUtilityService = commonUtilityService;
        this.cookieSettingModalService = cookieSettingModalService;
        this.digitalData = digitalData;
        this.gdsButtonEventsService = gdsButtonEventsService;
        this.geolocationService = geolocationService;
        this.parser = parser;
        this.store = store;
        this.tcmService = tcmService;
        this.document = document;
        this.window = window;
        this.dataReadyNotification = new BehaviorSubject(undefined);
        this.dataReadyNotification$ = this.dataReadyNotification.asObservable();
        this.subscriptions = new Subscription();
        this.isDestroyed$ = new Subject();
        this.appEnvSrv.addDefaultValues(GDS_FOOTER_ENV_DEFAULTS, GDS_FOOTER_REPLACED_DEFAULTS);
        this.gdsFooterConfig = this.appEnvSrv.getEnvVariables('gds_footer');
        this.appConfig = this.appEnvSrv.getEnvVariables('app');
    }
    get isDataAvailable() {
        return !!this._isDataAvailable;
    }
    ngOnDestroy() {
        this.isDestroyed$.next(true);
        this.isDestroyed$.unsubscribe();
        this.subscriptions.unsubscribe();
    }
    init() {
        this.gdsFooterElem = this.document.querySelector('gds-footer');
        this.initGdsFooterListeners();
        if (!this.isDataAvailable) {
            this.store
                .pipe(select(view), takeUntil(this.isDestroyed$), filter((view) => {
                return view.ready;
            }))
                .subscribe((data) => {
                this._isDataAvailable = true;
                this.dataReadyNotification.next(data);
            });
            this.store.dispatch(getContentId());
        }
    }
    getContentId() {
        const defaultContentUrlInfo = {
            content_id: this.gdsFooterConfig.content_id,
            publication: this.tcmService.tridionEnvConfig.publication,
        };
        let urlBuilder$;
        if (this.gdsFooterConfig.use_tridion_config) {
            urlBuilder$ = this.tcmService.siteConfig$.pipe(map((config) => {
                let contentId = '';
                if (config) {
                    for (const [key, value] of Object.entries(config)) {
                        if (key.startsWith('global_footer')) {
                            contentId = value;
                        }
                    }
                }
                const publication = get(config, 'tridion_pub', '');
                const theme = get(config, 'theme', 'theme-default');
                this.gdsFooterElem?.setAttribute('theme', theme);
                return { content_id: contentId, publication };
            }));
        }
        else {
            const theme = get(this.appConfig, 'gds_theme', 'theme-default');
            this.gdsFooterElem?.setAttribute('theme', theme);
            urlBuilder$ = of(defaultContentUrlInfo);
        }
        return urlBuilder$;
    }
    logDdlEvents(eventAction, type, results) {
        if (this.appDataLayer.digitalDataEnabled) {
            const eventInfo = this.digitalData.getEventInfoInstance('Global Footer', eventAction, type, '', new Date().toISOString(), results, this.window.document.URL);
            const eventCategory = this.digitalData.getEventCategoryInstance(get(this.window, 'digitalData.page.category.primaryCategory', ''), get(this.window, 'digitalData.page.category.subCategory1', ''));
            const eventData = this.digitalData.getEventInstance(eventInfo, eventCategory);
            this.digitalData.fireEvent(eventData);
        }
    }
    initGdsFooterListeners() {
        if (!this.gdsFooterElem) {
            return;
        }
        this.gdsFooterElem.addEventListener('gdsComponentLoaded', (event) => {
            if (event.target.tagName.toLowerCase() === 'gds-footer-utility') {
                this.initPrivacyModal();
            }
        });
        this.gdsFooterElem.addEventListener('buttonElementClick', (event) => {
            this.gdsButtonEventsService.buttonClickHandler(event);
        });
        this.gdsFooterElem.addEventListener('buttonClicked', (event) => {
            this.gdsButtonEventsService.buttonClickHandler(event);
        });
    }
    initPrivacyModal() {
        this.subscriptions.add(this.parser.hasCookieSettingModal$.pipe(filter(Boolean)).subscribe(() => {
            this.cookieSettingModalService.init();
            this.setPrivacyIcon();
        }));
    }
    setPrivacyIcon() {
        if (!this.gdsFooterElem) {
            return;
        }
        const country = this.geolocationService.getGeoCountry() || 'us';
        if (country.toLowerCase() === 'us') {
            return;
        }
        this.gdsFooterElem
            .querySelectorAll('.ot-sdk-show-settings')
            .forEach((el) => {
            const gdsButton = el.closest('gds-button');
            const btnData = this.commonUtilityService.getComponentData(gdsButton);
            if (gdsButton && btnData) {
                const newBtnData = {
                    ...btnData,
                    svgOptions: null,
                    label: el.textContent,
                };
                gdsButton.setAttribute('data', JSON.stringify(newBtnData));
            }
        });
    }
}
GdsFooterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsFooterService, deps: [{ token: i1$1.AppDataLayerService }, { token: i2.AppEnvironmentService }, { token: i3.GdsCommonUtilityService }, { token: i4.CookieSettingModalService }, { token: i1$1.DigitalDataService }, { token: i5.GdsButtonEventsService }, { token: i6.GeolocationService }, { token: GdsFooterParserService }, { token: i8.Store }, { token: i9.TcmService }, { token: DOCUMENT }, { token: WINDOW }], target: i0.ɵɵFactoryTarget.Injectable });
GdsFooterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsFooterService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsFooterService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.AppDataLayerService }, { type: i2.AppEnvironmentService }, { type: i3.GdsCommonUtilityService }, { type: i4.CookieSettingModalService }, { type: i1$1.DigitalDataService }, { type: i5.GdsButtonEventsService }, { type: i6.GeolocationService }, { type: GdsFooterParserService }, { type: i8.Store }, { type: i9.TcmService }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: Window, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }]; } });

class GdsFooterEffects {
    constructor(actions$, gdsFooterService, gdsFooterParser, tcmService) {
        this.actions$ = actions$;
        this.gdsFooterService = gdsFooterService;
        this.gdsFooterParser = gdsFooterParser;
        this.tcmService = tcmService;
        this.getContentId$ = createEffect(() => this.actions$.pipe(ofType(getContentId), switchMap(() => this.gdsFooterService.getContentId().pipe(map((contentUrlInfo) => {
            if (!contentUrlInfo.content_id) {
                return getContentIdFailure({
                    error: 'Failed to get GDS Footer content Id',
                });
            }
            return getContentIdSuccess({
                contentUrlInfo,
            });
        }), catchError((error) => of(getContentIdFailure({ error })))))));
        this.getContentIdFailure$ = createEffect(() => this.actions$.pipe(ofType(getContentIdFailure), tap((error) => {
            this.gdsFooterService.logDdlEvents('Error', 'Failed to load content Id', error);
        })), { dispatch: false });
        this.getContent$ = createEffect(() => this.actions$.pipe(ofType(getContentIdSuccess), switchMap(({ contentUrlInfo }) => from(this.tcmService.requestContentById(contentUrlInfo.content_id, contentUrlInfo.publication)).pipe(map((content) => getContentSuccess({ content: content[0] })), catchError((error) => of(getContentFailure({ error })))))));
        this.getContentFailure$ = createEffect(() => this.actions$.pipe(ofType(getContentFailure), tap((error) => {
            this.gdsFooterService.logDdlEvents('Error', 'Failed to load Tridion Content', error);
        })), { dispatch: false });
        this.parseContent$ = createEffect(() => this.actions$.pipe(ofType(getContentSuccess), switchMap(({ content }) => {
            const parsedContent = this.gdsFooterParser.parseTridionData(content);
            if (!parsedContent) {
                return of(parseContentFailure({
                    error: 'Failed to parse content!',
                }));
            }
            return of(parseContentSuccess({ parsedContent }));
        })));
    }
}
GdsFooterEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsFooterEffects, deps: [{ token: i1$2.Actions }, { token: GdsFooterService }, { token: GdsFooterParserService }, { token: i9.TcmService }], target: i0.ɵɵFactoryTarget.Injectable });
GdsFooterEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsFooterEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsFooterEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$2.Actions }, { type: GdsFooterService }, { type: GdsFooterParserService }, { type: i9.TcmService }]; } });

const initialState = {
    tridionContent: {
        contentUrlInfo: null,
        rawContent: {},
    },
    view: {
        ready: false,
        sections: [],
    },
};
const Reducers = createReducer(initialState, on(getContentIdSuccess, (state, { contentUrlInfo }) => ({
    ...state,
    tridionContent: {
        ...state.tridionContent,
        contentUrlInfo,
    },
})), on(getContentSuccess, (state, { content }) => ({
    ...state,
    loading: false,
    loaded: true,
    error: '',
    tridionContent: {
        ...state.tridionContent,
        rawContent: content,
    },
})), on(parseContentSuccess, (state, { parsedContent }) => ({
    ...state,
    view: {
        ready: parsedContent.sections.length > 0,
        sections: parsedContent.sections,
    },
})));

class GdsFooterModule {
}
GdsFooterModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsFooterModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
GdsFooterModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsFooterModule, imports: [GdsButtonEventsModule, i8.StoreFeatureModule, i1$2.EffectsFeatureModule] });
GdsFooterModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsFooterModule, providers: [GdsFooterService], imports: [[
            GdsButtonEventsModule,
            StoreModule.forFeature(FooterFeatureKey, Reducers),
            EffectsModule.forFeature([GdsFooterEffects]),
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsFooterModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [GdsFooterService],
                    imports: [
                        GdsButtonEventsModule,
                        StoreModule.forFeature(FooterFeatureKey, Reducers),
                        EffectsModule.forFeature([GdsFooterEffects]),
                    ],
                }]
        }] });

export { FooterFeatureKey, GDS_FOOTER_ENV_DEFAULTS, GdsFooterModule, GdsFooterService };
